import React, { useEffect, useState } from 'react';
import { ChildProfile } from '../../models/childProfile';
import { StaffProfile } from '../../models/staffProfile';
import ChildSelectCard from './ChildSelectCard';

interface Props {
  open: boolean;
  childProfiles: ChildProfile[];
  /** Current child default — seeds the modal's working selection on open. */
  initialChildId: number | null;
  /** True when the child default is currently driven by the sidebar filter.
   *  While the filter is on a specific child it owns the default, so the
   *  modal shows the child read-only; when the filter is "All" the child
   *  is editable here. */
  childFromFilter: boolean;
  staffProfiles: StaffProfile[];
  /** Current staff default — seeds the modal's working selection on open. */
  initialTeacherIds: number[];
  /** Current click-created session length — seeds the modal on open. */
  initialSessionLengthMinutes: number;
  /** True when the staff default was explicitly set via this modal (pinned).
   *  Pinned staff survive the automatic defaults snapshot that normally
   *  follows every session save. */
  teachersPinned: boolean;
  /** Commit the working selections. Staff become the pinned default. */
  onSave: (updates: {
    childId: number | null;
    teacherIds: number[];
    sessionLengthMinutes: number;
  }) => void;
  /** Unpin — the staff default goes back to following the last-saved session. */
  onResetAuto: () => void;
  onClose: () => void;
}

const SessionDefaultsModal: React.FC<Props> = ({
  open,
  childProfiles,
  initialChildId,
  childFromFilter,
  staffProfiles,
  initialTeacherIds,
  initialSessionLengthMinutes,
  teachersPinned,
  onSave,
  onResetAuto,
  onClose,
}) => {
  const [childId, setChildId] = useState<number | null>(initialChildId);
  const [teacherIds, setTeacherIds] = useState<number[]>(initialTeacherIds);
  const [sessionLengthMinutes, setSessionLengthMinutes] = useState(initialSessionLengthMinutes);
  const [staffQuery, setStaffQuery] = useState('');

  // Re-seed the working selections each time the modal opens, so a Cancel on
  // a previous visit doesn't leak stale edits into this one.
  useEffect(() => {
    if (open) {
      setChildId(initialChildId);
      setTeacherIds(initialTeacherIds);
      setSessionLengthMinutes(initialSessionLengthMinutes);
      setStaffQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const toggleTeacher = (id: number) => {
    setTeacherIds(prev =>
      prev.includes(id) ? prev.filter(tid => tid !== id) : [...prev, id],
    );
  };

  const sq = staffQuery.trim().toLowerCase();
  const filteredStaff = sq
    ? staffProfiles.filter(s => s.name.toLowerCase().includes(sq))
    : staffProfiles;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-defaults-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          // Flat light-ivory workbench surface — same family as .rtb-form /
          // .org-settings, in opaque form because it floats over the scrim.
          background: 'linear-gradient(180deg, #fffefa 0%, #faf8f1 100%)',
          border: '1px solid rgba(38, 146, 63, 0.16)',
          borderRadius: 12, padding: '1.25rem 1.5rem',
          width: 'min(440px, 92vw)',
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h6 id="session-defaults-title" className="mb-1" style={{ fontWeight: 700 }}>
          Defaults for new sessions
        </h6>
        <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
          Applied to each new session you draw on the grid. Existing sessions
          are not affected.
        </p>

        {/* Child — editable when the sidebar filter is on "All"; while the
            filter is on a specific child the filter owns the default, so
            the card renders read-only. Same hero chip + dropdown as the
            session editor (ChildSelectCard). */}
        <div style={{ marginBottom: '1rem' }}>
          <ChildSelectCard
            childProfiles={childProfiles}
            activeChildId={childId}
            onSelect={setChildId}
            readOnly={childFromFilter}
            emptyLabel="No default child"
            noneLabel="No default child"
          />
          {childFromFilter && (
            <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 6 }}>
              Set by the child filter &mdash; switch the filter to All to
              choose a different default here.
            </div>
          )}
        </div>

        {/* Staff */}
        <div style={{ marginBottom: '0.5rem' }}>
          <span className="wsg-sdp__label">Staff</span>
          <div
            role="listbox"
            aria-multiselectable="true"
            style={{
              border: '1px solid rgba(38, 146, 63, 0.22)', borderRadius: 8,
              background: 'rgba(255, 255, 255, 0.55)',
              maxHeight: 220, overflowY: 'auto',
            }}
          >
            <div className="wsg-sdp__search wsg-sdp__search--inline">
              <i className="fas fa-search" aria-hidden />
              <input
                type="text"
                placeholder="Search staff…"
                value={staffQuery}
                onChange={(e) => setStaffQuery(e.target.value)}
                aria-label="Search staff"
              />
            </div>
            {staffProfiles.length === 0 ? (
              <div style={{ padding: '0.6rem 0.75rem', fontSize: '0.85rem', color: '#888' }}>
                No editors available
              </div>
            ) : filteredStaff.length === 0 ? (
              <div style={{ padding: '0.6rem 0.75rem', fontSize: '0.85rem', color: '#888' }}>
                No matches
              </div>
            ) : (
              filteredStaff.map(s => {
                const selected = teacherIds.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => toggleTeacher(s.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '0.45rem 0.75rem',
                      background: selected ? '#26923f10' : 'transparent',
                      border: 'none', borderBottom: '1px solid rgba(38, 146, 63, 0.10)',
                      cursor: 'pointer', textAlign: 'left', fontSize: '0.88rem',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 26, height: 26, borderRadius: '50%',
                        background: s.colourCode, color: '#fff',
                        fontSize: '0.68rem', fontWeight: 700, flexShrink: 0,
                      }}
                    >
                      {s.initials}
                    </span>
                    <span style={{ flex: 1 }}>{s.name}</span>
                    {selected && (
                      <i className="fas fa-check" style={{ color: '#26923f' }} aria-hidden />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Session length — applied when the user single-clicks a grid cell
            to add a session. Click-and-drag sizes by grid slots instead. */}
        <div style={{ margin: '0.75rem 0 0.5rem' }}>
          <span className="wsg-sdp__label">Session length</span>
          <select
            className="form-select form-select-sm"
            value={sessionLengthMinutes}
            onChange={(e) => setSessionLengthMinutes(Number(e.target.value))}
            aria-label="Default session length"
          >
            {[30, 45, 60, 90, 120].map(m => (
              <option key={m} value={m}>
                {m >= 60 ? `${m / 60} hour${m > 60 ? 's' : ''}` : `${m} minutes`}
              </option>
            ))}
          </select>
          <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 4 }}>
            Used when you click once to add a session. Click-and-drag sizes
            the session as you drag.
          </div>
        </div>

        {/* Location preset — hidden until the app supports locations on
            sessions. Flip this guard when it does. */}
        {false && (
          <div style={{ marginBottom: '0.5rem' }}>
            <span className="wsg-sdp__label">Location</span>
            <select disabled style={{ width: '100%' }}>
              <option>No location assigned</option>
            </select>
          </div>
        )}

        {teachersPinned && (
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: '0.78rem', color: '#666', marginTop: '0.75rem',
            }}
          >
            <i className="fas fa-thumbtack" style={{ color: '#26923f' }} aria-hidden />
            <span style={{ flex: 1 }}>
              Staff default is pinned — saving sessions won&rsquo;t change it.
            </span>
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              style={{ fontSize: '0.78rem' }}
              onClick={onResetAuto}
              title="Staff will follow the most recently saved session again"
            >
              Reset to automatic
            </button>
          </div>
        )}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button
            type="button"
            className="btn btn-link btn-sm text-muted"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => onSave({ childId, teacherIds, sessionLengthMinutes })}
          >
            Save defaults
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionDefaultsModal;
